import { useState, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { formatAmount } from '@/lib/currency';

interface PaymentFormProps {
  onSuccess: (paymentIntentId: string) => void;
  onProcessing?: (paymentIntentId: string) => void;
  onError: (error: string) => void;
  amount: number;
  disabled?: boolean;
}

function PaymentFormContent({ onSuccess, onProcessing, onError, amount, disabled }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setErrorMessage('');

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message || 'An error occurred');
      onError(error.message || 'An error occurred');
      setProcessing(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    } else if (paymentIntent && paymentIntent.status === 'requires_action') {
      // Handle additional authentication if needed
      setErrorMessage('Additional authentication required. Please try again.');
      onError('Additional authentication required');
      setProcessing(false);
    } else if (paymentIntent && paymentIntent.status === 'processing') {
      // ACH payments go to processing state - call onProcessing if available, otherwise fall back to onSuccess
      if (onProcessing) {
        onProcessing(paymentIntent.id);
      } else {
        onSuccess(paymentIntent.id);
      }
    } else {
      setErrorMessage('Unexpected payment status');
      onError('Unexpected payment status');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{
          layout: 'tabs',
        }}
      />

      {errorMessage && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {errorMessage}
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || !elements || processing || disabled}
        className="w-full"
        size="lg"
      >
        {processing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : (
          `Pay ${formatAmount(amount)}`
        )}
      </Button>
    </form>
  );
}

interface StripePaymentFormProps {
  clientSecret: string;
  stripeAccountId: string;
  onSuccess: (paymentIntentId: string) => void;
  onProcessing?: (paymentIntentId: string) => void;
  onError: (error: string) => void;
  amount: number;
  disabled?: boolean;
}

export function StripePaymentForm({
  clientSecret,
  stripeAccountId,
  onSuccess,
  onProcessing,
  onError,
  amount,
  disabled,
}: StripePaymentFormProps) {
  const stripePromise = useMemo(
    () => loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '', {
      stripeAccount: stripeAccountId,
    }),
    [stripeAccountId]
  );

  if (!clientSecret) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#0f172a',
          },
        },
      }}
    >
      <PaymentFormContent
        onSuccess={onSuccess}
        onProcessing={onProcessing}
        onError={onError}
        amount={amount}
        disabled={disabled}
      />
    </Elements>
  );
}
